/**
 * The permission vocabulary the portal gates on. `PERMISSION_KEYS` mirrors the DB catalog
 * (`select key from permissions order by sort_order`); the smoke compares the two. Copy (label, help)
 * lives in the locale files under `permissions.<key>.label|help` and `permissionGroups.<group>`.
 */

export const ROLES = ['admin', 'manager', 'dispatcher', 'accounting'] as const;
export type Role = (typeof ROLES)[number];

export const PERMISSION_GROUPS = ['owner', 'carrier', 'truck', 'driver', 'broker', 'credit', 'invoice', 'receivable', 'team', 'owner_fee'] as const;
export type PermissionGroup = (typeof PERMISSION_GROUPS)[number];

export const PERMISSION_KEYS = [
  'owner.manage',
  'carrier.create',
  'carrier.edit',
  'carrier.edit_terms',
  'carrier.set_status',
  'truck.manage',
  'truck.set_status',
  'truck.assign_driver',
  'driver.edit',
  'driver.set_status',
  'broker.manage',
  'broker_setup.manage',
  // 0131 (broker credit): record a credit check; record, resolve and reopen broker incidents. The override
  // of a blocked broker is NOT a key: the agency admin only (hard-coded, `agency.admin`).
  'broker_credit.record',
  'broker_incident.manage',
  // 0069: the two ledgers. The invoice list itself stays admin + accounting (RLS), whatever the matrix says.
  'invoice.record_payment',
  'invoice.reverse_payment',
  'invoice.write_off',
  'receivable.mark_invoiced',
  'receivable.record_payment',
  'receivable.dispute',
  'receivable.correct',
  // 0074: the office team. Default: admin only for invite and manage, managers also assign carriers.
  'member.invite',
  'member.manage',
  'dispatcher.assign_carriers',
  // 0095 (owner-fees): leased trucks and the weekly owner statements. Every writer ALSO needs the money
  // role (admin or accounting): a granted key never lets a manager or a dispatcher write money it cannot read.
  'fee_settings.edit',
  'lease.manage',
  'owner_statement.manage',
  'owner_statement.finalize',
  'owner_statement.cancel',
] as const;
export type PermissionKey = (typeof PERMISSION_KEYS)[number];

/** Hard-coded server rules that are NOT in the matrix: the role alone decides. */
export const ROLE_RULES = {
  /** The permission matrix and the agency settings. */
  'agency.admin': ['admin'],
  /** app.can_manage_driver: mint and revoke app codes. */
  'driver.invite': ['admin', 'manager', 'dispatcher'],
  /** carrier_dispatch_plans_sel: the dispatch % column; also the gate of carrier_set_dispatch_plan and
   *  truck_set_dispatch_plan (0026), so the portal offers the Dispatch % dialog to the same roles. */
  'dispatch_pct.read': ['admin', 'accounting'],
  /** driver_pay_plans_sel and driver_set_pay_plan (0055/0056): a driver's pay plan per seat. */
  'driver_pay.manage': ['admin', 'accounting'],
  /** app.can_run_loads (portal-loads, F13): create, dispatch, answer by phone, advance, stops, price,
   *  documents, cancel. Accounting reads every load and runs none. */
  'load.run': ['admin', 'manager', 'dispatcher'],
  /** The RLS of load_positions (0087): the driver's live position. Accounting reads zero rows, so the
   *  portal does not even ask (portal-address-map 5.1). */
  'position.read': ['admin', 'manager', 'dispatcher'],
  /** invoice_read (INVOKER on the invoices RLS) and invoice_raise / invoice_send: the carrier invoice
   *  list and its writers. Everyone else reads zero rows and is refused (F12 of the e2e run). */
  'invoice.read': ['admin', 'accounting'],
  /** The owner-fee money gate (load_fees_sel, 0095): leases, owner statements, fee settings. */
  'owner_fee.read': ['admin', 'accounting'],
  /** reports (0110, 0111): the agency's cut in a report (agency fee, carrier revenue, invoices) and the
   *  money KPI tiles. The RPCs answer NULL for everyone else; this only decides what the portal draws. */
  'report.money': ['admin', 'accounting'],
  /** app.can_write_fleet_document (0127): carrier, truck and driver documents. A dispatcher only for the
   *  carriers she runs (the server checks the carrier; the checklist only lists what she can see). */
  'document.manage': ['admin', 'manager', 'dispatcher'],
  /** The money kinds (dispatch agreement, void check, Zelle): documents_sel reads and the writer allow
   *  admin and accounting only (0126). */
  'document.money': ['admin', 'accounting'],
  /** invoice-pdf (0145): agency_profile_read answers admin and accounting (the bank details of the weekly
   *  invoice); agency_profile_update and the delivery settings are `agency.admin`. */
  'company.read': ['admin', 'accounting'],
} as const satisfies Record<string, readonly Role[]>;
export type RoleRule = keyof typeof ROLE_RULES;

export type Gate = PermissionKey | RoleRule;

export type PermissionSource = 'admin' | 'agency' | 'default';

/** One row of `agency_permissions(p_agency)`, camelCased. */
export interface PermissionRow {
  role: Role;
  permission: string;
  groupKey: string;
  sortOrder: number;
  allowed: boolean;
  source: PermissionSource;
}

export function isRole(v: unknown): v is Role {
  return typeof v === 'string' && (ROLES as readonly string[]).includes(v);
}

export function isPermissionKey(v: unknown): v is PermissionKey {
  return typeof v === 'string' && (PERMISSION_KEYS as readonly string[]).includes(v);
}

export function isRoleRule(v: unknown): v is RoleRule {
  return typeof v === 'string' && v in ROLE_RULES;
}
